import ganttChartService from "../../../service/ganttChartService";
import { useAppSelector } from "../../../store/hooks";
import {
  Employee,
  TransportationTask,
  WorkTask,
} from "../../../types/ganttChart";
import { ganttChartVerticalValues } from "../../../utils/ganttChartValues";
import VerticalOperationalTask from "./VerticalOperationalTasks";
import VerticalGanttChartTransportationTask from "./VerticalGanttChartTransportationTask";
import VerticalWorkTask from "./VerticalWorkTask";
import UnplannedTaskChartPositionAllocationService, {
  GroupedTask,
} from "../../../service/UnplannedTaskChartPositionAllocationService";
import DragAndDropTask from "../dragAndDrop/DragAndDropTask";
import { transportTypeAt } from "../../../utils/transportSegmentUtil";

// Tasks are visually positioned by start time already (see getYPosition below), but were
// previously rendered as three separate type-grouped blocks. That put them in the DOM/tab
// order grouped by type instead of chronologically, so keyboard users tabbed through every
// visit, then every operational task, then every transport task. Merging into one
// chronologically-sorted list fixes tab order without changing the (independently
// absolute-positioned) visual layout. Array.sort is stable, so operational tasks that share
// a start time keep their pre-sorted ganttChartPosition order relative to each other, which
// is what keeps their SVG paint/stacking order correct when they overlap.
type RenderableTask =
  | { kind: "work"; task: WorkTask }
  | { kind: "operational"; task: GroupedTask }
  | { kind: "transportation"; task: TransportationTask };

const VerticalGanttChartTaskWrapper = ({
  xPosition,
  employee,
  useColorGroupingsAsTeamSplitter,
}: {
  xPosition: number;
  employee: Employee;
  useColorGroupingsAsTeamSplitter: boolean;
}) => {
  const { verticalColumnWidth } = useAppSelector(
    (state) => state.ganttChartSlice,
  );
  const { timelineRange, pixelPerMinute } = useAppSelector(
    (state) => state.timelineSlice,
  );
  const { isDragging } = useAppSelector((state) => state.dragAndDropSlice);

  const getYPosition = (startTime: string) => {
    return ganttChartVerticalValues.findTaskYPosition(
      timelineRange!,
      pixelPerMinute,
      startTime,
    );
  };

  const getHeight = (startTime: string, endTime: string) => {
    return (
      ganttChartService.timeDifferenceInMinutes(startTime, endTime) *
      pixelPerMinute
    );
  };

  /* Cluster tasks and set ganttChartIndex for each */
  const overlappingClustersOfOperationalTasks =
    UnplannedTaskChartPositionAllocationService.clusterOverlappingTasks(
      employee.tasks.operationalTasks,
      (task) => new Date(task.start),
      (task) => new Date(task.end),
    );

  /* Sort tasks according to ganttChartIndex, as svg files are drawn in an order.
   This ensures that smaller tasks are shown on top. Cannot be handled with css on svg elements. */
  const sortedArrayOfClusteredOperationalTasks =
    overlappingClustersOfOperationalTasks.sort(
      (a, b) => a.ganttChartPosition - b.ganttChartPosition,
    );

  const chronologicalTasks: RenderableTask[] = [
    ...employee.tasks.workTasks.map((task): RenderableTask => ({
      kind: "work",
      task,
    })),
    ...sortedArrayOfClusteredOperationalTasks.map((task): RenderableTask => ({
      kind: "operational",
      task,
    })),
    ...employee.tasks.transportationTasks.map((task): RenderableTask => ({
      kind: "transportation",
      task,
    })),
  ].sort((a, b) => (a.task.start < b.task.start ? -1 : 1));

  return (
    <>
      <>
        {chronologicalTasks.map((entry, index) => {
          switch (entry.kind) {
            case "work":
              return (
                <VerticalWorkTask
                  employee={employee}
                  key={"nexus-worktask" + index}
                  x={xPosition}
                  task={entry.task}
                  y={getYPosition(entry.task.start)}
                  width={ganttChartVerticalValues.getTaskWidth(
                    verticalColumnWidth,
                  )}
                  height={getHeight(entry.task.start, entry.task.end)}
                  useColorGroupingsAsTeamSplitter={
                    useColorGroupingsAsTeamSplitter
                  }
                />
              );
            case "operational":
              return (
                <VerticalOperationalTask
                  employee={employee}
                  key={"nexus-operationaltask" + index}
                  xPosition={xPosition}
                  task={entry.task}
                  yPosition={getYPosition(entry.task.start)}
                  width={ganttChartVerticalValues.getTaskWidth(
                    verticalColumnWidth,
                  )}
                  height={getHeight(entry.task.start, entry.task.end)}
                />
              );
            case "transportation":
              return (
                <VerticalGanttChartTransportationTask
                  employee={employee}
                  key={"nexus-transportationtask" + index}
                  xPosition={xPosition}
                  task={entry.task}
                  yPosition={getYPosition(entry.task.start)}
                  transportationType={transportTypeAt(
                    employee.transportationType,
                    entry.task.start,
                  )}
                  width={ganttChartVerticalValues.getTaskWidth(
                    verticalColumnWidth,
                  )}
                />
              );
          }
        })}

        {employee.shiftSchedule.lockedForPlanning && (
          <div
            style={{
              position: "absolute",
              left: xPosition,
              top: 0,
              width: ganttChartVerticalValues.getOverlayWidth(verticalColumnWidth),
              height: "100%",
              backgroundColor: "var(--col-graph-first-1)",
              opacity: 0.2,
              pointerEvents: "none",
            }}
          />
        )}
        {/* The task being dragged, when in drag and drop mode. */}
        {isDragging && <DragAndDropTask />}
      </>
    </>
  );
};

export default VerticalGanttChartTaskWrapper;
